import { useState } from "react";
import { useNavigate, Navigate, Link as RouterLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { defaultRouteFor } from "@/components/ProtectedRoute";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (!loading && user) return <Navigate to={defaultRouteFor(user.role)} replace />;

  const submit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome, ${u.name}`);
      navigate(defaultRouteFor(u.role));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-[#fdfcf7]">
      {/* Left: editorial hero */}
      <div className="hidden lg:flex flex-col justify-between bg-[#1f4d2e] text-stone-100 p-14 relative overflow-hidden">
        <div className="absolute inset-0 glow-bg" />
        <div className="absolute inset-0 grid-bg opacity-30" />

        <div className="relative z-10 flex items-center gap-4">
          <img
            src="/taskmaster-logo.jpg"
            alt="Taskmaster Philippines"
            className="h-16 w-16 rounded-md object-cover ring-1 ring-stone-100/20"
          />
          <div>
            <div className="font-display text-3xl font-medium tracking-tight leading-none text-stone-50">
              Taskmaster Philippines
            </div>
            <div className="font-display-italic text-sm text-[#d4a82a] mt-2">
              Your Virtual Assistance, Your Virtual services
            </div>
          </div>
        </div>

        <div className="relative z-10 space-y-10 max-w-xl">
          <div>
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="gold-rule" />
              <span className="text-[10px] tracking-[0.32em] uppercase text-[#d4a82a]">Welcome</span>
              <span className="gold-rule" />
            </div>
            <h1 className="font-display text-6xl xl:text-7xl font-normal leading-[1.05] tracking-tight text-stone-50">
              Experience
              <br />
              <span className="font-display-italic text-[#d4a82a]">Comfort &amp; Focus</span>
            </h1>
            <div className="mt-8 flex items-center gap-3 text-[#d4a82a]/80">
              <span className="h-px bg-[#d4a82a]/40 w-12" />
              <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><path d="M5 0l1 4 4 1-4 1-1 4-1-4-4-1 4-1z"/></svg>
              <span className="h-px bg-[#d4a82a]/40 w-12" />
            </div>
            <p className="mt-8 text-stone-300/90 leading-[1.7] text-[15px] max-w-md font-light">
              A refined workforce platform for businesses of every kind —
              capture screenshots, measure focus, and pay only for productive hours.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-8 pt-6 border-t border-stone-100/15">
            {[
              { k: "Tracker", v: "Live" },
              { k: "Roles", v: "Four" },
              { k: "Exports", v: "CSV · PDF" },
            ].map((s) => (
              <div key={s.k}>
                <div className="text-[10px] tracking-[0.28em] uppercase text-stone-400 mb-1.5">{s.k}</div>
                <div className="font-display text-xl text-stone-50">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-[10px] tracking-[0.3em] uppercase text-stone-500">
          <span className="text-[#d4a82a]">✦</span>&nbsp; Time, considered.
        </div>
      </div>

      {/* Right: form */}
      <div className="flex flex-col justify-center p-6 sm:p-12 bg-[#fdfcf7]">
        <div className="w-full max-w-sm mx-auto">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <img
              src="/taskmaster-logo.jpg"
              alt="Taskmaster Philippines"
              className="h-12 w-12 rounded-md object-cover ring-1 ring-stone-200"
            />
            <div>
              <div className="font-display text-lg font-semibold tracking-tight text-[#1f4d2e] leading-tight">
                Taskmaster Philippines
              </div>
              <div className="font-display-italic text-[11px] text-stone-500 leading-tight mt-0.5">
                Your Virtual Assistance, Your Virtual services
              </div>
            </div>
          </div>

          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase font-medium text-[#1f4d2e] mb-4">
            <span className="h-px bg-[#d4a82a] w-6" />
            Sign in
          </div>
          <h2 className="font-display text-5xl font-normal tracking-tight mb-2 text-[#1f4d2e] leading-none">
            Welcome <span className="font-display-italic text-[#d4a82a]">back</span>
          </h2>
          <p className="text-stone-600 text-[14px] leading-relaxed mt-4 mb-8 font-light">
            Enter your credentials to continue.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase font-medium text-stone-600">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-md border-stone-200 bg-white focus-visible:ring-1 focus-visible:ring-[#1f4d2e] focus-visible:border-[#1f4d2e] h-11 transition-elegant"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase font-medium text-stone-600">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-md border-stone-200 bg-white focus-visible:ring-1 focus-visible:ring-[#1f4d2e] focus-visible:border-[#1f4d2e] h-11 transition-elegant"
                placeholder="••••••••"
              />
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full h-12 rounded-md bg-[#d4a82a] hover:bg-[#b8902a] text-[#1f4d2e] font-semibold tracking-wide group transition-elegant"
            >
              {submitting ? "Signing in…" : (
                <span className="inline-flex items-center gap-2">
                  Sign in <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-stone-200 text-center">
            <p className="text-sm text-stone-600">
              Don't have an account?{" "}
              <RouterLink
                to="/register"
                data-testid="link-to-register"
                className="font-semibold text-[#1f4d2e] hover:text-[#d4a82a] transition-elegant"
              >
                Create one
              </RouterLink>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
